export type ReviewPhase = 'idle' | 'submitting' | 'stale_reselect';

export type ReviewState = {
  selectedAccountPairIds: ReadonlySet<string>;
  selectedTransactionPairIds: ReadonlySet<string>;
  accountDecisions: ReadonlyMap<string, 'same' | 'distinct'>;
  transactionDecisions: ReadonlyMap<string, 'same' | 'distinct'>;
  annotationDialog: { pairKey: string; selectedAnchorTransactionId: string | null } | null;
  manualPair: { oldCursor: string | null; newCursor: string | null; oldTransactionId: string | null; newTransactionId: string | null } | null;
  phase: ReviewPhase;
};

export type ReviewAction =
  | { type: 'toggleAccountPair'; pairId: string }
  | { type: 'toggleTransactionPair'; pairId: string }
  | { type: 'setAccountDecision'; pairId: string; decision: 'same' | 'distinct' }
  | { type: 'removeAccountDecision'; pairId: string }
  | { type: 'setTransactionDecision'; pairId: string; decision: 'same' | 'distinct' }
  | { type: 'removeTransactionDecision'; pairId: string }
  | { type: 'openAnnotationDialog'; pairKey: string }
  | { type: 'chooseAnnotationAnchor'; anchorTransactionId: string }
  | { type: 'closeAnnotationDialog' }
  | { type: 'setManualOldCursor'; cursor: string | null }
  | { type: 'setManualNewCursor'; cursor: string | null }
  | { type: 'chooseManualOld'; transactionId: string | null }
  | { type: 'chooseManualNew'; transactionId: string | null }
  | { type: 'beginMutation' }
  | { type: 'endMutation' }
  | { type: 'staleReselect' }
  | { type: 'freshSuccess' };

const isOpaqueToken = (value: string | null): boolean =>
  value === null || (value.trim().length > 0 && ![...value].some((char) => {
    const code = char.codePointAt(0) ?? 0;
    return code < 32 || (code >= 127 && code <= 159);
  }));

export function createInitialReviewState(): ReviewState {
  return {
    selectedAccountPairIds: new Set(),
    selectedTransactionPairIds: new Set(),
    accountDecisions: new Map(),
    transactionDecisions: new Map(),
    annotationDialog: null,
    manualPair: null,
    phase: 'idle',
  };
}

function withToggledValue(values: ReadonlySet<string>, value: string): ReadonlySet<string> {
  const next = new Set(values);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function withSelectedValue(values: ReadonlySet<string>, value: string): ReadonlySet<string> {
  if (values.has(value)) return values;
  const next = new Set(values);
  next.add(value);
  return next;
}

function withoutDecision(values: ReadonlyMap<string, 'same' | 'distinct'>, key: string): ReadonlyMap<string, 'same' | 'distinct'> {
  if (!values.has(key)) return values;
  const next = new Map(values);
  next.delete(key);
  return next;
}

function withoutSelection(values: ReadonlySet<string>, key: string): ReadonlySet<string> {
  if (!values.has(key)) return values;
  const next = new Set(values);
  next.delete(key);
  return next;
}

function selectionAllowed(state: ReviewState): boolean {
  return state.phase === 'idle';
}

export function reviewStateReducer(state: ReviewState, action: ReviewAction): ReviewState {
  if (action.type === 'staleReselect') {
    return { ...createInitialReviewState(), phase: 'stale_reselect' };
  }
  if (action.type === 'freshSuccess') {
    return state.phase === 'stale_reselect' ? { ...createInitialReviewState(), phase: 'idle' } : state;
  }
  if (action.type === 'beginMutation') {
    return state.phase === 'idle' ? { ...state, phase: 'submitting' } : state;
  }
  if (action.type === 'endMutation') {
    return state.phase === 'submitting' ? { ...state, phase: 'idle' } : state;
  }
  if (!selectionAllowed(state)) return state;

  switch (action.type) {
    case 'toggleAccountPair':
      return isOpaqueToken(action.pairId) && action.pairId !== null
        ? state.selectedAccountPairIds.has(action.pairId)
          ? { ...state, selectedAccountPairIds: withToggledValue(state.selectedAccountPairIds, action.pairId), accountDecisions: withoutDecision(state.accountDecisions, action.pairId) }
          : { ...state, selectedAccountPairIds: withToggledValue(state.selectedAccountPairIds, action.pairId) }
        : state;
    case 'toggleTransactionPair':
      return isOpaqueToken(action.pairId) && action.pairId !== null
        ? state.selectedTransactionPairIds.has(action.pairId)
          ? { ...state, selectedTransactionPairIds: withToggledValue(state.selectedTransactionPairIds, action.pairId), transactionDecisions: withoutDecision(state.transactionDecisions, action.pairId) }
          : { ...state, selectedTransactionPairIds: withToggledValue(state.selectedTransactionPairIds, action.pairId) }
        : state;
    case 'setAccountDecision':
      if (!isOpaqueToken(action.pairId) || action.pairId === null) return state;
      return { ...state, selectedAccountPairIds: withSelectedValue(state.selectedAccountPairIds, action.pairId), accountDecisions: new Map(state.accountDecisions).set(action.pairId, action.decision) };
    case 'removeAccountDecision':
      return isOpaqueToken(action.pairId) && action.pairId !== null
        ? { ...state, selectedAccountPairIds: withoutSelection(state.selectedAccountPairIds, action.pairId), accountDecisions: withoutDecision(state.accountDecisions, action.pairId) }
        : state;
    case 'setTransactionDecision':
      if (!isOpaqueToken(action.pairId) || action.pairId === null) return state;
      return { ...state, selectedTransactionPairIds: withSelectedValue(state.selectedTransactionPairIds, action.pairId), transactionDecisions: new Map(state.transactionDecisions).set(action.pairId, action.decision) };
    case 'removeTransactionDecision':
      return isOpaqueToken(action.pairId) && action.pairId !== null
        ? { ...state, selectedTransactionPairIds: withoutSelection(state.selectedTransactionPairIds, action.pairId), transactionDecisions: withoutDecision(state.transactionDecisions, action.pairId) }
        : state;
    case 'openAnnotationDialog':
      return isOpaqueToken(action.pairKey) && action.pairKey !== null
        ? { ...state, annotationDialog: { pairKey: action.pairKey, selectedAnchorTransactionId: null } }
        : state;
    case 'chooseAnnotationAnchor':
      return state.annotationDialog && isOpaqueToken(action.anchorTransactionId) && action.anchorTransactionId !== null
        ? { ...state, annotationDialog: { ...state.annotationDialog, selectedAnchorTransactionId: action.anchorTransactionId } }
        : state;
    case 'closeAnnotationDialog':
      return state.annotationDialog === null ? state : { ...state, annotationDialog: null };
    case 'setManualOldCursor':
      return isOpaqueToken(action.cursor) ? { ...state, manualPair: { oldCursor: action.cursor, newCursor: state.manualPair?.newCursor ?? null, oldTransactionId: state.manualPair?.oldTransactionId ?? null, newTransactionId: state.manualPair?.newTransactionId ?? null } } : state;
    case 'setManualNewCursor':
      return isOpaqueToken(action.cursor) ? { ...state, manualPair: { oldCursor: state.manualPair?.oldCursor ?? null, newCursor: action.cursor, oldTransactionId: state.manualPair?.oldTransactionId ?? null, newTransactionId: state.manualPair?.newTransactionId ?? null } } : state;
    case 'chooseManualOld':
      return isOpaqueToken(action.transactionId) ? { ...state, manualPair: { oldCursor: state.manualPair?.oldCursor ?? null, newCursor: state.manualPair?.newCursor ?? null, oldTransactionId: action.transactionId, newTransactionId: state.manualPair?.newTransactionId ?? null } } : state;
    case 'chooseManualNew':
      return isOpaqueToken(action.transactionId) ? { ...state, manualPair: { oldCursor: state.manualPair?.oldCursor ?? null, newCursor: state.manualPair?.newCursor ?? null, oldTransactionId: state.manualPair?.oldTransactionId ?? null, newTransactionId: action.transactionId } } : state;
    default:
      return state;
  }
}
